// TMMFX-3571 — password reset for the viewed user, opened from the View / Edit User page header.
import {useEffect, useState} from "react";
import {
    Alert,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    FormControl,
    FormControlLabel,
    Radio,
    RadioGroup,
    TextField,
    Typography,
} from "@mui/material";
import {useResetLocalPasswordAdminPortal} from "../../../hooks/useResetLocalPasswordAdminPortal";
import {useSendLocalPasswordResetEmailAdminPortal} from "../../../hooks/useSendLocalPasswordResetEmailAdminPortal";
import {RESET_PASSWORD_OPTIONS} from "../../../constants";

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const defaultRecipient = (login, email) => (EMAIL_RE.test(login ?? "") ? login : email ?? "");

const extractErrorMessage = (err) => {
    const data = err?.response?.data;
    if (typeof data === "string") {
        return data;
    }
    if (data) {
        const message = data.message || "";
        const details = data.details ?? data.detail ?? "";
        if (message && details) {
            return `${message} (${details})`;
        }
        if (message || details) {
            return message || details;
        }
    }
    return err?.message || "Failed to reset password. Please try again.";
};

const ResetPasswordDialog = ({open, onClose, userId, login, email}) => {
    const [option, setOption] = useState(RESET_PASSWORD_OPTIONS.GENERATE_PASSWORD);
    const [password, setPassword] = useState("");
    const [confirmPassword, setConfirmPassword] = useState("");
    const [recipient, setRecipient] = useState("");
    const [error, setError] = useState("");
    const [submitted, setSubmitted] = useState(null);

    const resetLocalPasswordAdminPortalMutation = useResetLocalPasswordAdminPortal();
    const sendLocalPasswordResetEmailMutation = useSendLocalPasswordResetEmailAdminPortal();

    const clearState = () => {
        setOption(RESET_PASSWORD_OPTIONS.GENERATE_PASSWORD);
        setPassword("");
        setConfirmPassword("");
        setRecipient("");
        setError("");
        setSubmitted(null);
    };

    const handleClose = () => {
        clearState();
        onClose();
    };

    useEffect(() => {
        if (!open) return;
        clearState();
        setRecipient(defaultRecipient(login, email));
    }, [open, login, email]);

    const handleOptionChange = (e) => {
        setOption(e.target.value);
        setPassword("");
        setConfirmPassword("");
        setError("");
        setSubmitted(null);
        setRecipient(defaultRecipient(login, email));
    };

    const isPending = resetLocalPasswordAdminPortalMutation.isPending || sendLocalPasswordResetEmailMutation.isPending;

    const canSubmit =
        option === RESET_PASSWORD_OPTIONS.GENERATE_PASSWORD
            ? !!password && !!confirmPassword && password === confirmPassword
            : !!recipient && EMAIL_RE.test(recipient);

    const handleSubmit = () => {
        setError("");
        if (option === RESET_PASSWORD_OPTIONS.GENERATE_PASSWORD) {
            resetLocalPasswordAdminPortalMutation.mutate(
                {id: userId, password},
                {
                    onSuccess: () => setSubmitted({type: "password", value: password}),
                    onError: (err) => setError(extractErrorMessage(err)),
                }
            );
        } else {
            sendLocalPasswordResetEmailMutation.mutate(
                {email: recipient, login},
                {
                    onSuccess: () => setSubmitted({type: "link", value: recipient}),
                    onError: (err) => setError(extractErrorMessage(err)),
                }
            );
        }
    };

    const title = submitted?.type === "password" ? `New password for ${login}` : `Reset password for ${login}`;

    return (
        <Dialog open={open} onClose={handleClose} fullWidth maxWidth="sm">
            <DialogTitle>{title}</DialogTitle>
            {submitted ? (
                <>
                    <DialogContent>
                        {submitted.type === "password" ? (
                            <>
                                <Alert severity="success">The password has been updated successfully.</Alert>
                                <Typography variant="body1" sx={{mt: 2}}>
                                    New Password: {submitted.value}
                                </Typography>
                            </>
                        ) : (
                            <Alert severity="success">A password reset link has been sent to {submitted.value}.</Alert>
                        )}
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={handleClose} variant="contained" color="primary">
                            {submitted.type === "password" ? "Confirm" : "Close"}
                        </Button>
                    </DialogActions>
                </>
            ) : (
                <>
                    <DialogContent>
                        <DialogContentText sx={{marginBottom: "5px"}}>Choose an option to reset the user password.</DialogContentText>

                        <FormControl component="fieldset">
                            <RadioGroup aria-label="reset-option" name="resetOption" value={option} onChange={handleOptionChange}>
                                <FormControlLabel value={RESET_PASSWORD_OPTIONS.GENERATE_PASSWORD} control={<Radio />} label="Enter new password" />
                                <FormControlLabel value={RESET_PASSWORD_OPTIONS.SEND_EMAIL} control={<Radio />} label="Send reset link to user's email" />
                            </RadioGroup>
                        </FormControl>

                        {option === RESET_PASSWORD_OPTIONS.GENERATE_PASSWORD && (
                            <>
                                <DialogContentText sx={{marginBottom: "5px"}}>Enter and confirm the new password for this user.</DialogContentText>
                                <TextField
                                    autoFocus
                                    margin="dense"
                                    label="Enter Password"
                                    type="password"
                                    fullWidth
                                    required
                                    value={password}
                                    onChange={(e) => {
                                        setPassword(e.target.value);
                                        if (error) setError("");
                                    }}
                                />
                                <TextField
                                    margin="dense"
                                    label="Re-enter Password"
                                    type="password"
                                    fullWidth
                                    required
                                    value={confirmPassword}
                                    onChange={(e) => {
                                        setConfirmPassword(e.target.value);
                                        if (error) setError("");
                                    }}
                                    error={confirmPassword.length > 0 && password !== confirmPassword}
                                    helperText={confirmPassword.length > 0 && password !== confirmPassword ? "Passwords do not match" : ""}
                                />
                            </>
                        )}

                        {option === RESET_PASSWORD_OPTIONS.SEND_EMAIL && (
                            <>
                                <DialogContentText sx={{marginBottom: "5px"}}>Send a password reset link to this user.</DialogContentText>
                                <TextField
                                    autoFocus
                                    margin="dense"
                                    label="Email Address"
                                    type="email"
                                    fullWidth
                                    required
                                    value={recipient}
                                    onChange={(e) => {
                                        setRecipient(e.target.value);
                                        if (error) setError("");
                                    }}
                                />
                                <Typography variant="caption" color="text.secondary">
                                    {EMAIL_RE.test(login ?? "")
                                        ? "Prefilled from the user's login, which is a valid email address."
                                        : `Prefilled from the user's email address — their login (${login}) is not an email address.`}
                                </Typography>
                            </>
                        )}

                        {error && <Alert severity="error" sx={{mt: 2}}>{error}</Alert>}
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={handleClose}>Cancel</Button>
                        <Button onClick={handleSubmit} variant="contained" color="primary" disabled={!canSubmit || isPending}>
                            {isPending ? "Submitting…" : "Submit"}
                        </Button>
                    </DialogActions>
                </>
            )}
        </Dialog>
    );
};

export default ResetPasswordDialog;
